import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { KeyRound, X, ShieldCheck, Check, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { sortAccessUsers } from '../sortUtils';

// Keep these in sync with backend BOARD_ORGS
const BOARD_ORGS = [
  { key: 'programs', label: 'TNS Programs' },
  { key: 'housing', label: 'TNS Housing Corp' },
];

export default function AccessTab({ api }) {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const [granted, all] = await Promise.all([
        api.get('/board/access/users'),
        api.get('/employees/all'),
      ]);
      setUsers(granted.data || []);
      setAllUsers((all.data || []).filter(u => u.is_active !== false));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const grantedIds = new Set(users.map(u => u.id));
  const candidates = (allUsers || []).filter(u =>
    !grantedIds.has(String(u.id)) &&
    (u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
  );
  candidates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const candidatesSlice = candidates.slice(0, 20);

  const setBusyFor = (id, v) => setBusy(prev => ({ ...prev, [id]: v }));

  const grantOrg = async (u, org) => {
    setBusyFor(u.id, true);
    try {
      await api.post('/board/access/grant', {
        user_id: u.id,
        org,
        grant: true,
        board_role: u.board_role || 'director',
      });
      const orgLabel = BOARD_ORGS.find(o => o.key === org)?.label;
      toast.success(`Granted ${orgLabel} board access to ${u.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusyFor(u.id, false);
  };

  const revokeOrg = async (u, org) => {
    const orgLabel = BOARD_ORGS.find(o => o.key === org)?.label;
    if (!window.confirm(`Revoke ${u.name}'s access to ${orgLabel}? They'll still keep access to any other board they're a member of.`)) return;
    setBusyFor(u.id, true);
    try {
      await api.post('/board/access/grant', {
        user_id: u.id,
        org,
        grant: false,
      });
      toast.success(`Revoked ${orgLabel} access`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusyFor(u.id, false);
  };

  const toggleHide = async (u) => {
    // Toggle the hide_from_scheduling flag by re-granting their first org with the new flag.
    // (The backend grant endpoint accepts hide_from_scheduling as an override.)
    const accessOrgs = u.board_access_orgs && u.board_access_orgs.length ? u.board_access_orgs : ['programs'];
    setBusyFor(u.id, true);
    try {
      await api.post('/board/access/grant', {
        user_id: u.id,
        org: accessOrgs[0],
        grant: true,
        board_role: u.board_role || 'director',
        hide_from_scheduling: !u.hide_from_scheduling,
      });
      toast.success(u.hide_from_scheduling ? `${u.name} will now appear in scheduling` : `${u.name} hidden from scheduling`);
      load();
    } catch (e) {
      toast.error('Failed');
    }
    setBusyFor(u.id, false);
  };

  const userOrgs = (u) => {
    const arr = u.board_access_orgs && u.board_access_orgs.length
      ? u.board_access_orgs
      : (u.board_access ? ['programs'] : []);
    return new Set(arr);
  };

  return (
    <div className="space-y-4" data-testid="access-tab">
      <div>
        <h2 className="text-lg font-bold text-[#2D1B4E]">Board Portal Access</h2>
        <p className="text-xs text-[#5A4D6E]">Each board (TNS Programs · TNS Housing Corp) has its own access list — grant or revoke them independently per user.</p>
      </div>

      {/* === Users with access === */}
      <Card className="border-[#DED8EB] bg-white">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-[#2D1B4E] mb-3"><ShieldCheck className="h-4 w-4 inline mr-1 text-[#43A047]" /> Users with Board Access ({users.length})</h3>
          {users.length === 0 ? (
            <p className="text-xs text-[#8E85A0] text-center py-4">No users granted board access yet.</p>
          ) : (
            <div className="space-y-1.5">
              {sortAccessUsers(users).map(u => {
                const orgs = userOrgs(u);
                return (
                  <div key={u.id} className="flex items-center justify-between bg-[#F7F4EF] rounded px-3 py-2 gap-3 flex-wrap" data-testid={`granted-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#2D1B4E]">{u.name}</p>
                      <p className="text-[10px] text-[#8E85A0]">{u.email} · {u.role} · {u.board_role || 'director'}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Per-board pill toggles */}
                      <div className="flex items-center gap-1" data-testid={`org-pills-${u.id}`}>
                        {BOARD_ORGS.map(org => {
                          const granted = orgs.has(org.key);
                          return (
                            <button
                              key={org.key}
                              onClick={() => granted ? revokeOrg(u, org.key) : grantOrg(u, org.key)}
                              disabled={busy[u.id]}
                              className={`text-[10px] font-medium px-2 py-0.5 rounded border transition-all flex items-center gap-1
                                ${granted
                                  ? 'bg-[#E8F5E9] text-[#1B5E20] border-[#43A047] hover:bg-[#FDECEA] hover:text-[#D9534F] hover:border-[#D9534F]'
                                  : 'bg-white text-[#8E85A0] border-[#E8E4EE] hover:border-[#6B3FA0] hover:text-[#6B3FA0]'}`}
                              title={granted ? `Revoke ${org.label} access` : `Grant ${org.label} access`}
                              data-testid={`org-toggle-${u.id}-${org.key}`}>
                              {granted ? <Check className="h-2.5 w-2.5" /> : <Building2 className="h-2.5 w-2.5" />}
                              {org.label}
                            </button>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-1.5 text-[10px] cursor-pointer text-[#5A4D6E] select-none" title="When ON, this user does not appear in Staff or scheduling lists.">
                        <input type="checkbox" checked={!!u.hide_from_scheduling}
                          onChange={() => toggleHide(u)} disabled={busy[u.id]}
                          className="h-3 w-3 accent-[#6B3FA0]"
                          data-testid={`hide-sched-${u.id}`} />
                        Hide from scheduling
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[10px] text-[#8E85A0] mt-3 italic">Click a green pill to revoke that board, or a gray pill to grant it. Revoking all boards removes the user from the access list above.</p>
        </CardContent>
      </Card>

      {/* === Grant new === */}
      <Card className="border-[#DED8EB] bg-white">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-[#2D1B4E] mb-2"><KeyRound className="h-4 w-4 inline mr-1 text-[#6B3FA0]" /> Grant Access to a User</h3>
          <p className="text-xs text-[#8E85A0] mb-3">Search existing app users (staff, supervisors, admins). For external board members without a login, create a User account first under Staff.</p>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…" className="mb-3" data-testid="access-search" />
          {candidatesSlice.length === 0 ? (
            <p className="text-xs text-[#8E85A0] text-center py-4">{search ? 'No matches.' : 'Start typing to find a user.'}</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {candidatesSlice.map(u => (
                <div key={u.id} className="flex items-center justify-between border border-[#F0EDF6] rounded px-3 py-2 gap-2 flex-wrap" data-testid={`candidate-${u.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2D1B4E]">{u.name}</p>
                    <p className="text-[10px] text-[#8E85A0]">{u.email} · {u.role}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {BOARD_ORGS.map(org => (
                      <Button
                        key={org.key}
                        size="sm"
                        onClick={() => grantOrg(u, org.key)}
                        disabled={busy[u.id]}
                        className="bg-[#6B3FA0] hover:bg-[#5A2F8F] h-7 text-[11px]"
                        data-testid={`grant-${u.id}-${org.key}`}>
                        <Building2 className="h-3 w-3 mr-1" /> {org.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
